import type { CaptureResult } from "posthog-js";

// Outlook SafeLinks / Office365 link-scanner sends phantom promise-rejection events
// from Chrome/Windows-7 user-agents on Azure IPs — they are never real user errors.
// ResizeObserver loop noise is a benign browser quirk with zero actionable signal.
// Bare "undefined" rejections carry no information and only clutter the error stream.
// "Script error." is the browser's sanitised cross-origin error message — no stack,
// no filename, no actionable info. PostHog captures it via window.onerror before
// the addEventListener-based suppression in posthog-provider.tsx can intercept it.
// UID2: thrown by Grow by Mediavine's own bundle (`faves.grow.me/main.js` →
// `uid2SdkLoader-*.js`) when the browser refuses its `cdn.prod.uidapi.com`
// script tag. Dropped ONLY after the half we actually controlled was fixed:
// that host was missing from our enforced CSP from the day Grow shipped
// (2026-08-11), and adding it in #3833 cut the failure rate from 33.6% of
// pageviews to 14.9% (95/283 → 21/141 over matched 24h windows — ~4.7σ, not
// noise). The residual is client-side blocking we cannot reach: `uidapi.com`
// sits on standard tracker blocklists (EasyPrivacy), and the failures are
// uniform across Chrome, Mobile Safari, Samsung Internet and ~20 countries,
// with `reason: "error"` (instant refusal) and never `timeout` — the shape of
// a blocklist, and a rate consistent with ad-blocker prevalence.
//
// Suppressing is right here only BECAUSE the config half is fixed: an
// unactionable third-party failure was drowning the real signal 21:1 (21 UID2
// vs 1 genuine client error in the post-fix window; 95 vs 3 before). If Grow
// is ever removed, or its loader stops reaching uidapi.com, delete this line
// rather than letting it hide a future regression.
const NOISE_PATTERNS: RegExp[] = [
	/Object Not Found Matching Id:\d+, MethodName:update, ParamCount:4/, // Outlook SafeLinks crawler
	/ResizeObserver loop (limit exceeded|completed with undelivered notifications)/, // benign browser quirk
	/^Non-Error promise rejection captured with value: undefined$/, // no signal
	/^Script error\.$/, // cross-origin script error — browser-sanitised, no stack available
	/^UID2 SDK failed to load after \d+ attempt\(s\)/, // Grow/Mediavine ad-identity SDK blocked client-side — see above
];

// Sensitive query params that must never reach PostHog: auth tokens, magic-link
// codes, OAuth state, password-reset tokens, and post-auth redirect targets that
// frequently embed the same.
const SENSITIVE_QUERY_PARAMS = new Set([
	"token",
	"code",
	"state",
	"access_token",
	"id_token",
	"refresh_token",
	"otp",
	"verification_token",
	"reset_token",
	"magiclink",
	"magic_link",
	"hash",
	"email",
	"callbackurl",
	"callback_url",
	"redirect",
	"redirect_uri",
	"return_to",
	"returnto",
	"next",
]);

function matchesNoise(value: string): boolean {
	return NOISE_PATTERNS.some((pattern) => pattern.test(value));
}

function exceptionValuesFromEvent(event: CaptureResult): string[] {
	const out: string[] = [];

	const legacy = event.properties?.$exception_values;
	if (Array.isArray(legacy)) {
		for (const v of legacy) if (typeof v === "string") out.push(v);
	}

	// posthog-js >=1.155 emits structured exceptions under $exception_list.
	const list = event.properties?.$exception_list;
	if (Array.isArray(list)) {
		for (const entry of list) {
			if (entry && typeof entry === "object") {
				const value = (entry as Record<string, unknown>).value;
				if (typeof value === "string") out.push(value);
			}
		}
	}

	const message = event.properties?.$exception_message;
	if (typeof message === "string") out.push(message);

	return out;
}

export function shouldDropEvent(event: CaptureResult | null): boolean {
	if (event === null || event.event !== "$exception") return false;
	return exceptionValuesFromEvent(event).some(matchesNoise);
}

export function sanitizeUrl(input: string): string {
	try {
		const url = new URL(input);
		const toRedact: string[] = [];
		url.searchParams.forEach((_, key) => {
			if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) toRedact.push(key);
		});
		if (toRedact.length === 0) return input;
		for (const key of toRedact) url.searchParams.set(key, "[redacted]");
		return url.toString();
	} catch {
		return input;
	}
}

function sanitizeEventUrls(event: CaptureResult): CaptureResult {
	const props = event.properties;
	if (!props) return event;

	const urlKeys = ["$current_url", "$referrer", "$pathname"] as const;
	let mutated = false;
	const next: Record<string, unknown> = { ...props };
	for (const key of urlKeys) {
		const value = props[key];
		if (typeof value === "string") {
			const cleaned = sanitizeUrl(value);
			if (cleaned !== value) {
				next[key] = cleaned;
				mutated = true;
			}
		}
	}
	return mutated ? { ...event, properties: next } : event;
}

/**
 * True when the current browser advertises itself as automated
 * (`navigator.webdriver`) — Playwright, Selenium, headless Chrome, etc. Used to
 * drop non-exception client events at the source: 23,756 bot "visitors" were
 * polluting `consent_banner_shown` (100% `$virt_is_bot` on ingestion), diluting
 * every funnel denominator. Exceptions still pass through so real crashes hit
 * by automated crawlers stay visible.
 */
function isAutomatedBrowser(): boolean {
	return typeof navigator !== "undefined" && navigator.webdriver === true;
}

let cachedSignals: string[] | null = null;

/**
 * Cheap synchronous stealth-automation tells. TAG only, never drop: stealth
 * fleets hide `navigator.webdriver`, so the drop above misses them. Memoized,
 * one WebGL probe per page load, no network.
 */
export function automationSignals(): string[] {
	if (cachedSignals) return cachedSignals;
	const out: string[] = [];
	if (typeof navigator === "undefined") return out;
	const ua = navigator.userAgent ?? "";
	if (navigator.webdriver === true) out.push("webdriver");
	if (/HeadlessChrome/i.test(ua)) out.push("headless_ua");
	if (navigator.languages?.length === 0) out.push("no_languages");
	if (
		/Chrome\//.test(ua) &&
		!/Mobile/.test(ua) &&
		(navigator.plugins?.length ?? 0) === 0
	)
		out.push("no_plugins_desktop_chrome");
	if (typeof window !== "undefined") {
		if (window.outerWidth === 0 || window.outerHeight === 0)
			out.push("zero_outer");
	}
	if (typeof screen !== "undefined") {
		const { width, height } = screen;
		if (
			(width === 2000 && height === 2000) ||
			(width === 1280 && height === 1280)
		)
			out.push("square_screen");
	}
	try {
		const gl = document.createElement("canvas").getContext("webgl");
		const ext = gl?.getExtension("WEBGL_debug_renderer_info");
		const renderer = ext ? gl?.getParameter(ext.UNMASKED_RENDERER_WEBGL) : "";
		if (/SwiftShader|llvmpipe|Mesa OffScreen/i.test(String(renderer ?? "")))
			out.push("software_gl");
	} catch {
		// WebGL unavailable or blocked: no signal.
	}
	cachedSignals = out;
	return out;
}

/**
 * `human_v1` client traffic class (see `HUMAN_V1_HOGQL` in `./trust`).
 * `bot` = automation suspect; `human` = this tab session showed a real
 * interaction (click/scroll/keydown); otherwise `unclassified` — NOT human.
 * A 2nd pageview alone no longer upgrades (2026-10-26): a stealth fleet that
 * hides `navigator.webdriver` loads pages without ever scrolling, and a real
 * visitor reaches a 2nd page by clicking a link, which already upgrades. The server/HogQL side owns the final verdict
 * (logged-in persons always count as human there, whatever this says).
 */
export type ClientTrafficClass = "bot" | "human" | "unclassified";

const HUMAN_KEY = "ld_tc_human";
let listenersInstalled = false;

// sessionStorage throws SecurityError in some webviews / sandboxed frames:
// every access is wrapped, and a failure simply leaves the visitor
// `unclassified` (never `bot`).
function readSession(key: string): string | null {
	try {
		return window.sessionStorage.getItem(key);
	} catch {
		return null;
	}
}

function writeSession(key: string, value: string): void {
	try {
		window.sessionStorage.setItem(key, value);
	} catch {
		// Storage unavailable: no upgrade persisted.
	}
}

function markHuman(): void {
	writeSession(HUMAN_KEY, "1");
}

function installInteractionListeners(): void {
	if (listenersInstalled || typeof window === "undefined") return;
	listenersInstalled = true;
	const opts = { once: true, passive: true, capture: true } as const;
	try {
		for (const type of ["click", "scroll", "keydown"] as const) {
			window.addEventListener(type, markHuman, opts);
		}
	} catch {
		// Exotic embed without event listeners: stays `unclassified`.
	}
}

/** Browser IANA timezone, for server-side tz-vs-IP-country checks. */
export function browserTimezone(): string | null {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
	} catch {
		return null;
	}
}

export function classifyClientTraffic(
	_event: CaptureResult,
	automationSuspect: boolean,
): ClientTrafficClass {
	if (automationSuspect) return "bot";
	if (typeof window === "undefined") return "unclassified";
	installInteractionListeners();
	return readSession(HUMAN_KEY) === "1" ? "human" : "unclassified";
}

export function postHogBeforeSend(
	event: CaptureResult | null,
): CaptureResult | null {
	if (event === null) return null;
	if (shouldDropEvent(event)) return null;
	if (event.event !== "$exception" && isAutomatedBrowser()) return null;
	const sanitized = sanitizeEventUrls(event);
	const signals = automationSignals();
	const suspect =
		signals.length >= 2 ||
		signals.includes("headless_ua") ||
		signals.includes("software_gl");
	return {
		...sanitized,
		properties: {
			...sanitized.properties,
			automation_signals: signals,
			automation_suspect: suspect,
			traffic_class: classifyClientTraffic(sanitized, suspect),
			browser_tz: browserTimezone(),
		},
	};
}

/** Test seam: clears the memoized signals and classifier state. */
export function resetAutomationSignalsCache(): void {
	cachedSignals = null;
	listenersInstalled = false;
}
